import { createSelector } from '@reduxjs/toolkit';

import { RootState } from '@verantos-dev/app-state-base';

import { COHORT_BUILDER_SLICE_KEY } from '../constants/constants';
import type { CohortBuilderState } from '../types';

const selectCohortBuilderState = (state: RootState) => state.cohortBuilder;

/** Cohort Definition Selector */
export const selectCohortDefinition = createSelector(
  selectCohortBuilderState,
  (cohortBuilderState) => cohortBuilderState?.cohortDefinition
);

/** Study Period Restrictions Selectors */
export const selectStudyPeriodRestrictions = createSelector(
  selectCohortDefinition,
  (cohortDefinition) => cohortDefinition?.studyPeriodRestrictions
);

export const selectStudyPeriodRestrictionsChangeStatus = createSelector(
  selectStudyPeriodRestrictions,
  (studyPeriodRestrictions) => studyPeriodRestrictions?.changeStatus
);
export const selectStudyPeriodRestrictionsChangeBy = createSelector(
  selectStudyPeriodRestrictions,
  (studyPeriodRestrictions) => studyPeriodRestrictions?.changedBy
);

/** Study Period Restriction Selectors */
export const selectOverallStudyPeriodRestriction = createSelector(
  selectStudyPeriodRestrictions,
  (studyPeriodRestrictions) => studyPeriodRestrictions?.overallStudyPeriodRestriction
);
export const selectOverallStudyPeriodRestrictionIsEnabled = createSelector(
  selectOverallStudyPeriodRestriction,
  (overallStudyPeriodRestriction) => overallStudyPeriodRestriction?.isEnabled
);
export const selectOverallStudyPeriodRestrictionStartDate = createSelector(
  selectOverallStudyPeriodRestriction,
  (overallStudyPeriodRestriction) => overallStudyPeriodRestriction?.startDate
);
export const selectOverallStudyPeriodRestrictionEndDate = createSelector(
  selectOverallStudyPeriodRestriction,
  (overallStudyPeriodRestriction) => overallStudyPeriodRestriction?.endDate
);

/** Baseline Period Restriction Selectors */
export const selectBaselinePeriodRestriction = createSelector(
  selectStudyPeriodRestrictions,
  (studyPeriodRestrictions) => studyPeriodRestrictions?.baselinePeriodRestriction
);
export const selectBaselinePeriodRestrictionIsEnabled = createSelector(
  selectBaselinePeriodRestriction,
  (baselinePeriodRestriction) => baselinePeriodRestriction?.isEnabled
);
export const selectBaselinePeriodRestrictionDaysBeforeIndexDate = createSelector(
  selectBaselinePeriodRestriction,
  (baselinePeriodRestriction) => baselinePeriodRestriction?.daysBeforeIndexDate
);

/** Follow Up Period Restriction Selectors */
export const selectFollowUpPeriodRestriction = createSelector(
  selectStudyPeriodRestrictions,
  (studyPeriodRestrictions) => studyPeriodRestrictions?.followUpPeriodRestriction
);
export const selectFollowUpPeriodRestrictionIsEnabled = createSelector(
  selectFollowUpPeriodRestriction,
  (followUpPeriodRestriction) => followUpPeriodRestriction?.isEnabled
);
export const selectFollowUpPeriodRestrictionDaysAfterIndexDate = createSelector(
  selectFollowUpPeriodRestriction,
  (followUpPeriodRestriction) => followUpPeriodRestriction?.daysAfterIndexDate
);

/** Index Date Selectors */
export const selectIndexDate = createSelector(
  selectCohortDefinition,
  (cohortDefinition) => cohortDefinition?.indexDate
);
export const selectIndexDateId = createSelector(selectIndexDate, (indexDate) => indexDate?.id);
export const selectIndexDateType = createSelector(selectIndexDate, (indexDate) => indexDate?.type);
export const selectIndexDateValue = createSelector(
  selectIndexDate,
  (indexDate) => indexDate?.indexDateValue
);
export const selectIndexDateEvent = createSelector(
  selectIndexDate,
  (indexDate) => indexDate?.indexDateEvent
);
export const selectIndexDateChangeStatus = createSelector(
  selectIndexDate,
  (indexDate) => indexDate?.changeStatus
);
export const selectIndexDateChangeBy = createSelector(
  selectIndexDate,
  (indexDate) => indexDate?.changedBy
);

/** Analytical Variables Selectors */
export const selectAnalyticalVariables = createSelector(
  selectCohortDefinition,
  (cohortDefinition) => cohortDefinition?.analyticalVariables
);

/** Subject Variables Selectors */
export const selectSubjectVariables = createSelector(
  selectCohortDefinition,
  (cohortDefinition) => cohortDefinition?.subjectVariables
);

export const selectSubjectVariableById = createSelector(
  [selectSubjectVariables, (state, id?: string) => id],
  (subjectVariables, id) => {
    return subjectVariables?.find((subjectVariable) => subjectVariable.id === id);
  }
);

/** Simple Events Selectors */
export const selectSimpleEvents = createSelector(
  selectCohortDefinition,
  (cohortDefinition) => cohortDefinition?.simpleEvents
);

/** Derived Events Selectors */
export const selectDerivedEvents = createSelector(
  selectCohortDefinition,
  (cohortDefinition) => cohortDefinition?.derivedEvents
);

export const selectEvents = createSelector(
  [selectSimpleEvents, selectDerivedEvents],
  (simpleEvents, derivedEvents) => [...(simpleEvents || []), ...(derivedEvents || [])]
);

export const selectIndexDateRange = createSelector(
  selectCohortDefinition,
  (cohortDefinition) => cohortDefinition?.indexDate?.indexDateRangeValue
);

export const selectCohortUpdatedByBuilder = (state: CohortBuilderState) => {
  return state?.[COHORT_BUILDER_SLICE_KEY].isCohortUpdatedByBuilder;
};

export const selectConceptGroupsConfidence = createSelector(
  selectCohortBuilderState,
  (cohortBuilderState) => cohortBuilderState?.conceptGroupsConfidence
);

// Base selector that returns all concept groups with warnings or errors (regardless of generated status)
export const selectAllConceptGroupsWithWarningsOrErrors = createSelector(
  selectConceptGroupsConfidence,
  (conceptGroupsConfidence) => {
    if (!conceptGroupsConfidence) return [];

    const conceptGroupsWithIssues: Array<{
      eventId: string;
      eventName: string;
      span: string;
      status: 'warning' | 'error';
      generated: boolean;
    }> = [];

    conceptGroupsConfidence.forEach((event) => {
      event.conceptGroups?.forEach((conceptGroup) => {
        if (conceptGroup.status === 'warning' || conceptGroup.status === 'error') {
          conceptGroupsWithIssues.push({
            eventId: event.eventId,
            eventName: event.eventName,
            span: conceptGroup.span,
            status: conceptGroup.status,
            generated: conceptGroup.generated ?? false,
          });
        }
      });
    });

    return conceptGroupsWithIssues;
  }
);

// Selector that filters out generated concept groups (for UI display)
export const selectConceptGroupsWithWarningsOrErrors = createSelector(
  selectAllConceptGroupsWithWarningsOrErrors,
  (allConceptGroupsWithIssues) => {
    return allConceptGroupsWithIssues.filter((cg) => !cg.generated);
  }
);

// Selector that only returns generated concept groups (for linking to events)
export const selectGeneratedConceptGroupsWithWarningsOrErrors = createSelector(
  selectAllConceptGroupsWithWarningsOrErrors,
  (allConceptGroupsWithIssues) => {
    return allConceptGroupsWithIssues.filter((cg) => cg.generated);
  }
);

export const selectQueuedEventModals = createSelector(
  selectCohortBuilderState,
  (cohortBuilderState) => cohortBuilderState?.queuedEventModals
);

export const selectQueuedEventModalsConceptGroupId = createSelector(
  selectCohortBuilderState,
  (cohortBuilderState) => cohortBuilderState?.queuedEventModalsConceptGroupId
);
